import { captureException } from "@web-monorepo/telemetry";
import type { EnrichedError } from "@web-monorepo/telemetry";
import { dojoFetch } from "./index";
import { sanitizeIds } from "./sanitizeIds";

/**
 * Result type for resource fetch operations.
 * Forces callers to explicitly handle both success and error cases.
 */
export type FetchResult<T> = { ok: true; value: T } | { ok: false; error: Error };

/**
 * Shared error handling wrapper for all fetch operations.
 * Handles try/catch, creates ResourceFetchError, and reports to Sentry.
 *
 * The `fn` callback receives the fetch Response. If the response is not ok,
 * throw a ResourceFetchError to let the wrapper handle it uniformly.
 */
export async function withFetchErrorHandling<T>(
  url: string,
  resourceType: string,
  fn: (response: Response) => Promise<T>,
  init?: RequestInit,
): Promise<FetchResult<T>> {
  try {
    const response = await dojoFetch(url, init);
    if (!response.ok) {
      const error = new ResourceFetchError(url, response.status, resourceType);
      captureException(error);
      return { ok: false, error };
    }
    const value = await fn(response);
    return { ok: true, value };
    // eslint-disable-next-line no-catch-all/no-catch-all
  } catch (err) {
    const error = err instanceof ResourceFetchError ? err : new ResourceFetchError(url, null, resourceType, err);
    captureException(error);
    return { ok: false, error };
  }
}

/**
 * Error class for resource fetch operations that implements EnrichedError
 * for rich Sentry context.
 */
export class ResourceFetchError extends Error implements EnrichedError {
  readonly url: string;
  readonly status: number | null;
  readonly resourceType: string;
  readonly tags: Record<string, string>;
  readonly contexts: Record<string, Record<string, unknown>>;
  readonly fingerprint: string[];

  constructor(url: string, status: number | null, resourceType: string, cause?: unknown) {
    const safeUrl = sanitizeIds(url.split("?")[0]);
    const message = status
      ? `Resource fetch failed: ${resourceType} from ${safeUrl} (status: ${status})`
      : `Resource fetch failed: ${resourceType} from ${safeUrl}`;

    super(message);
    this.name = "ResourceFetchError";
    this.url = safeUrl;
    this.status = status;
    this.resourceType = resourceType;

    if (cause instanceof Error) {
      this.cause = cause;
    }

    // Extract host and pathname for grouping (no query params)
    let host = "unknown";
    let pathname: string | undefined;
    let searchParamKeys: string[] | undefined;
    try {
      const base = typeof window !== "undefined" ? window.location.origin : "https://example.com";
      const parsed = new URL(url, base);
      host = parsed.host;
      pathname = parsed.pathname;
      searchParamKeys = [...parsed.searchParams.keys()];
      // eslint-disable-next-line no-catch-all/no-catch-all
    } catch {
      // URL parsing failed, use defaults
    }

    this.tags = {
      resourceType,
      host,
    };

    this.contexts = {
      request: {
        url: safeUrl,
        status,
        resourceType,
        ...(pathname && { pathname }),
        ...(searchParamKeys && { searchParamKeys }),
      },
    };

    this.fingerprint = ["resource-fetch-error", resourceType, host];
  }
}
